import { useCallback, useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, type PostCloseItem } from '../lib/api';
import { PostCloseCard } from '../pages/PostClose';
import Modal from './Modal';

/**
 * 마감 후 수정 승인 요청 팝업(2026-10-28 way) — 회계팀·인차팀 중 **내가 지금 승인할 수 있는** 기안이 있으면 뜬다.
 * 「나중에」 = 30분 · 새 기안이 오면 다시. 메뉴 배지 숫자도 이 맥박이 맞춘다(서버 `postClosePulse`).
 */

const POLL_MS = 60_000;
const SNOOZE_KEY = 'post-close-snooze-until';

let count = 0;
const subs = new Set<() => void>();
function setCount(n: number) {
  if (n === count) return;
  count = n;
  subs.forEach((f) => f());
}
export function usePostCloseCount(): number {
  return useSyncExternalStore((f) => { subs.add(f); return () => subs.delete(f); }, () => count);
}
/** 결정한 화면이 맥박을 바로 다시 돌리게. */
export function pokePostClose() {
  window.dispatchEvent(new Event('postclose:poke'));
}

function snoozed(): boolean {
  try { return Number(sessionStorage.getItem(SNOOZE_KEY) ?? 0) > Date.now(); } catch { return false; }
}
function snooze() {
  try { sessionStorage.setItem(SNOOZE_KEY, String(Date.now() + 30 * 60_000)); } catch { /* 못 쓰면 다음 맥박에 다시 뜬다 */ }
}

export function PostCloseAlerts() {
  const navigate = useNavigate();
  const [pop, setPop] = useState<PostCloseItem[] | null>(null);
  const seenSig = useRef('');

  const tick = useCallback(() => {
    api.postClosePulse().then((p) => {
      setCount(p.count);
      if (!p.items.length) { setPop(null); seenSig.current = ''; return; }
      const sig = p.items.map((i) => i.draftId).join(',');
      if (sig !== seenSig.current) {
        try { sessionStorage.removeItem(SNOOZE_KEY); } catch { /* 무시 */ }
        seenSig.current = sig;
      }
      if (!snoozed()) setPop(p.items);
    }).catch(() => { /* 알림은 없어도 업무 화면은 그대로 */ });
  }, []);

  useEffect(() => {
    tick();
    const t = setInterval(() => { if (document.visibilityState === 'visible') tick(); }, POLL_MS);
    const onVis = () => { if (document.visibilityState === 'visible') tick(); };
    window.addEventListener('focus', tick);
    window.addEventListener('postclose:poke', tick);
    document.addEventListener('visibilitychange', onVis);
    return () => {
      clearInterval(t);
      window.removeEventListener('focus', tick);
      window.removeEventListener('postclose:poke', tick);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, [tick]);

  if (!pop) return null;
  return (
    <Modal width="max-w-3xl">
      <div className="card space-y-3 p-5">
        <div>
          <div className="text-lg font-bold">🧾 마감 후 수정 승인 요청 ({pop.length}건)</div>
          <p className="mt-1 text-sm text-ink-soft">필요한 팀이 모두 승인하면 버스인웹에 바로 반영됩니다. 고칠 줄과 사유를 보고 승인하거나 반려해 주세요.</p>
        </div>
        <div className="max-h-[60vh] space-y-3 overflow-y-auto pr-1">
          {pop.map((it) => <PostCloseCard key={it.draftId} it={it} compact onDone={tick} />)}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button className="btn-ghost" onClick={() => { setPop(null); navigate('/post-close'); }}>마감 후 수정 화면에서 보기</button>
          <button className="btn-ghost ml-auto" onClick={() => { snooze(); setPop(null); }}>나중에</button>
        </div>
      </div>
    </Modal>
  );
}
